import { format } from "d3-format";
import React, { Component } from "react";
import PropTypes from "prop-types";

import displayValuesFor from "./displayValuesFor";
import GenericChartComponent from "../GenericChartComponent";

import ToolTipText from "./ToolTipText";
import ToolTipTSpanLabel from "./ToolTipTSpanLabel";
import { functor } from "../utils";

class ADXTooltip extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
  }

  renderSVG(moreProps) {
    const {
      onClick,
      origin: originProp,
      className,
      fontFamily,
      fontSize,
      labelFill,
      textFill,
      displayFormat,
      showSeries,
      showHistogram,
      yAccessor,
      options,
      appearance,
    } = this.props;

    const origin = functor(originProp);
    const {
      chartConfig: { width, height },
    } = moreProps;
    const [x, y] = origin(width, height);

    const currentItem = this.props.displayValuesFor(this.props, moreProps);
    const value = currentItem && yAccessor(currentItem);

    const adxValue =
      value && Number.isFinite(value.adx) ? displayFormat(value.adx) : "n/a";
    const plusValue =
      value && Number.isFinite(value.plusDI)
        ? displayFormat(value.plusDI)
        : "n/a";
    const minusValue =
      value && Number.isFinite(value.minusDI)
        ? displayFormat(value.minusDI)
        : "n/a";
    const histogramValue =
      value && Number.isFinite(value.histogram)
        ? displayFormat(value.histogram)
        : "n/a";

    const windowSize = options?.windowSize;
    const smoothingPeriod = options?.smoothingPeriod;

    const plusColor = appearance?.plusDI || "#00FF00";
    const minusColor = appearance?.minusDI || "#FF0000";
    const adxColor = appearance?.adx || "#FFD700";
    const histogramPositiveColor = appearance?.histogramPositive || "#00DD00";
    const histogramNegativeColor = appearance?.histogramNegative || "#FF0000";
    const histogramColor =
      value && value.histogram < 0
        ? histogramNegativeColor
        : histogramPositiveColor;

    return (
      <g
        className={className}
        transform={`translate(${x}, ${y})`}
        onClick={onClick}
        data-evo-id="tooltip-adx"
      >
        <ToolTipText
          fontFamily={fontFamily}
          fontSize={fontSize}
          fill={textFill}
        >
          <ToolTipTSpanLabel fill={labelFill}>ADX</ToolTipTSpanLabel>
          <ToolTipTSpanLabel fill={labelFill}>
            {` (${windowSize ?? "?"}`}
            {typeof smoothingPeriod !== "number" ? "" : `, ${smoothingPeriod}`}
            {`)`}
          </ToolTipTSpanLabel>
          <ToolTipTSpanLabel fill={labelFill}>: </ToolTipTSpanLabel>
          <tspan fill={adxColor}>{adxValue}</tspan>

          {showSeries && (
            <>
              <ToolTipTSpanLabel fill={labelFill}> +DI: </ToolTipTSpanLabel>
              <tspan fill={plusColor}>{plusValue}</tspan>
              <ToolTipTSpanLabel fill={labelFill}> -DI: </ToolTipTSpanLabel>
              <tspan fill={minusColor}>{minusValue}</tspan>
            </>
          )}

          {showHistogram && (
            <>
              <ToolTipTSpanLabel fill={labelFill}> Hist: </ToolTipTSpanLabel>
              <tspan fill={histogramColor}>{histogramValue}</tspan>
            </>
          )}
        </ToolTipText>
      </g>
    );
  }

  render() {
    return (
      <GenericChartComponent
        clip={false}
        svgDraw={this.renderSVG}
        drawOn={["mousemove"]}
      />
    );
  }
}

ADXTooltip.propTypes = {
  origin: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  className: PropTypes.string,
  fontFamily: PropTypes.string,
  fontSize: PropTypes.number,
  labelFill: PropTypes.string,
  textFill: PropTypes.string,
  showSeries: PropTypes.bool,
  showHistogram: PropTypes.bool,
  showShading: PropTypes.bool,
  yAccessor: PropTypes.func.isRequired,
  options: PropTypes.shape({
    windowSize: PropTypes.number,
    smoothingPeriod: PropTypes.number,
  }).isRequired,
  appearance: PropTypes.shape({
    plusDI: PropTypes.string,
    minusDI: PropTypes.string,
    adx: PropTypes.string,
    histogramPositive: PropTypes.string,
    histogramNegative: PropTypes.string,
  }),
  displayFormat: PropTypes.func,
  displayValuesFor: PropTypes.func,
  onClick: PropTypes.func,
};

ADXTooltip.defaultProps = {
  origin: [0, 0],
  className: "react-stockcharts-tooltip",
  displayFormat: format(".2f"),
  displayValuesFor: displayValuesFor,
  labelFill: "#4682B4",
  textFill: "#000000",
  showSeries: true,
  showHistogram: false,
  showShading: false,
};

export default ADXTooltip;
